// Instrumentación de analítica de producto para la PWA: Google Analytics 4
// (gtag.js) y Contentsquare (antes "Hotjar"; Hotjar fue adquirido por
// Contentsquare y su tag es el que da Session Replay / Heatmaps / Product
// Analytics). Pensado como una capa de "degradación elegante" igual que el
// resto de integraciones externas del repo (Stripe, Google Vision, Shazam):
//
//   - Si NO hay identificadores de build (VITE_GA_MEASUREMENT_ID /
//     VITE_CONTENTSQUARE_TAG_ID), el módulo NO carga ningún script de terceros
//     ni emite nada: coste cero y sin dependencias externas en el bundle.
//   - Los scripts se cargan de forma asíncrona (no bloquean el render) y SOLO
//     una vez (idempotente), aunque React monte dos veces en StrictMode.
//
// Privacidad: no se envía PII (email, nombre). La identificación de usuario usa
// identificadores opacos (sub de Cognito/rol). Contentsquare Session Replay se
// deja en su modo ANONIMIZADO por defecto (enmascara texto); el des-anonimizado
// solo debería habilitarse tras consentimiento explícito (ver setReplayConsent).
//
// CONSENTIMIENTO PREVIO (ePrivacy/GDPR): ningún script de terceros se carga
// hasta que el visitante CONCEDE la categoría `analytics` en el banner de
// cookies. `initAnalytics()` ya no carga nada por sí solo: se suscribe a los
// cambios de consentimiento y arranca/retira la instrumentación en consecuencia.

import { config } from '../config.js';
import { getConsent, isGranted, onConsentChange } from '../consent/cookieConsent.js';

// --- Tipado mínimo de las globales que inyectan los SDKs de terceros --------

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    // Contentsquare encola comandos en `_uxa` hasta que el tag remoto carga.
    _uxa?: unknown[];
  }
}

/** ¿Está configurado GA4 (hay Measurement ID de build)? */
export function hasGaConfig(): boolean {
  return config.gaMeasurementId.trim() !== '';
}

/** ¿Está configurado Contentsquare (hay Tag ID de build)? */
export function hasContentsquareConfig(): boolean {
  return config.contentsquareTagId.trim() !== '';
}

/** ¿Está configurado Google Ads (hay Ads ID de build, formato AW-...)? */
export function hasGoogleAdsConfig(): boolean {
  return config.googleAdsId.trim() !== '';
}

let gaLoaded = false;
let csLoaded = false;
// Última ruta enviada a Contentsquare, para no duplicar el pageview inicial
// (el tag emite un pageview "natural" al cargar) con el primer trackPageView.
let lastCsPath: string | null = null;

/**
 * Concede la ANALÍTICA en Consent Mode v2.
 *
 * IMPORTANTE: el tag base de Google (gtag.js, `window.gtag`/`dataLayer`, y los
 * `gtag('config', ...)` de GA4 + Google Ads) se carga de forma PÚBLICA desde el
 * index.html, arrancando con TODO 'denied'. Así la etiqueta es detectable por la
 * verificación de Google y puede medir desde la primera visita, sin escribir
 * cookies ni enviar datos personales hasta el consentimiento. Por eso aquí NO se
 * vuelve a cargar el script ni se redefine gtag: solo se eleva el consentimiento
 * a 'granted' cuando el usuario acepta analítica (y marketing para anuncios).
 */
function loadGa(): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (!hasGaConfig() && !hasGoogleAdsConfig()) return;
  gaLoaded = true; // marca que la analítica ha estado concedida al menos una vez

  // Consent Mode v2 → analítica concedida. El storage de ANUNCIOS lo gobierna la
  // categoría `marketing` (ver setAdsConsent, invocado desde applyConsent).
  // Idempotente: puede re-aplicarse si el usuario retira y vuelve a conceder.
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
  });
}

/**
 * Actualiza el estado de consentimiento de ANUNCIOS en Consent Mode v2. Se llama
 * cuando el usuario concede/retira la categoría `marketing` en el banner de
 * cookies. No-op si gtag aún no se cargó (el estado inicial ya lo fija loadGa).
 */
export function setAdsConsent(granted: boolean): void {
  if (!window.gtag) return;
  const v = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: v,
    ad_user_data: v,
    ad_personalization: v,
  });
}

/**
 * Inserta el tag de Contentsquare una sola vez. No-op si no hay Tag ID.
 * El tag se descarga de `//t.contentsquare.net/uxa/<tagId>.js` y procesa la
 * cola `window._uxa` que vamos rellenando con comandos (pageviews, eventos…).
 */
function loadContentsquare(): void {
  if (csLoaded || !hasContentsquareConfig() || typeof document === 'undefined') return;
  csLoaded = true;

  window._uxa = window._uxa || [];

  const s = document.createElement('script');
  s.async = true;
  // Protocol-relative: sirve el tag por https sin fijar el esquema a mano.
  s.src = `//t.contentsquare.net/uxa/${config.contentsquareTagId}.js`;
  document.head.appendChild(s);
}

/**
 * Aplica el consentimiento vigente a la instrumentación: carga los SDKs si el
 * usuario concedió `analytics`, y ajusta el Session Replay no anonimizado según
 * `replay`. Si no hay consentimiento de analítica, no se carga nada.
 *
 * Nota sobre retirada: GA/Contentsquare no ofrecen un "unload" limpio del script
 * una vez inyectado. Si el usuario RETIRA el consentimiento en la misma sesión,
 * dejamos de emitir eventos (GA Consent Mode a 'denied' y Contentsquare replay
 * withdrawn); la próxima carga de la app ya no inyectará los scripts.
 */
function applyConsent(): void {
  if (isGranted('analytics')) {
    loadGa();
    loadContentsquare();
    // Session Replay no anonimizado: solo si además concedió `replay`.
    setReplayConsent(isGranted('replay'));
    // Consentimiento de anuncios (Google Ads) según la categoría `marketing`.
    setAdsConsent(isGranted('marketing'));
  } else {
    // Retirada en caliente: cortar el envío de datos de GA si ya estaba cargado.
    if (gaLoaded && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
    // Contentsquare: retirar el replay no anonimizado si estaba activo.
    if (csLoaded) setReplayConsent(false);
  }
}

/**
 * Inicializa la analítica respetando el CONSENTIMIENTO del usuario. No carga
 * ningún script de terceros hasta que se concede la categoría `analytics`.
 * Idempotente: puede llamarse una vez al arranque; queda suscrito a los cambios
 * de consentimiento para cargar/retirar en caliente.
 */
export function initAnalytics(): void {
  // Aplica el estado actual (si ya había una decisión persistida).
  if (getConsent()) applyConsent();
  // Reacciona a cambios del banner (aceptar/retirar) sin recargar la página.
  onConsentChange(() => applyConsent());
}

/**
 * Registra una vista de página en una SPA. Debe llamarse en cada cambio de ruta.
 * @param path Ruta (pathname + search), p. ej. "/precios".
 * @param title Título de la página (opcional; por defecto el document.title).
 */
export function trackPageView(path: string, title?: string): void {
  if (hasGaConfig() && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: title ?? document.title,
    });
  }
  // Contentsquare: pageview "artificial" para SPAs. Se fija la ruta y se dispara
  // el pageview. Se omite si la ruta no cambió respecto al pageview anterior
  // (evita duplicar el pageview natural que el tag ya emite al cargar).
  if (hasContentsquareConfig() && window._uxa && path !== lastCsPath) {
    lastCsPath = path;
    window._uxa.push(['setPath', path]);
    window._uxa.push(['trackPageview', path]);
  }
}

/**
 * Asocia la sesión a un usuario con identificadores OPACOS (sin PII). Útil para
 * segmentar métricas por tenant/rol. Llamar tras autenticar.
 */
export function identifyUser(attrs: { userId: string; tenantId?: string; role?: string }): void {
  const { userId, tenantId, role } = attrs;
  if (hasGaConfig() && window.gtag) {
    // user_id de GA4 (vincula sesiones del mismo usuario entre dispositivos).
    window.gtag('set', { user_id: userId });
    window.gtag('set', 'user_properties', {
      ...(tenantId ? { tenant_id: tenantId } : {}),
      ...(role ? { role } : {}),
    });
  }
  if (hasContentsquareConfig() && window._uxa) {
    // Identificador de usuario de Contentsquare (clave opaca, sin email/nombre).
    window._uxa.push(['trackPageEvent', '@user-identifier@' + userId]);
    // Dimensiones de segmentación como variables dinámicas.
    if (tenantId) window._uxa.push(['trackDynamicVariable', { key: 'tenant_id', value: tenantId }]);
    if (role) window._uxa.push(['trackDynamicVariable', { key: 'role', value: role }]);
  }
}

/** Limpia la identificación al cerrar sesión (evita mezclar usuarios). */
export function resetIdentity(): void {
  if (hasGaConfig() && window.gtag) {
    window.gtag('set', { user_id: null });
    window.gtag('set', 'user_properties', { tenant_id: null, role: null });
  }
  // Contentsquare: cierra el ámbito del visitante actual para que la siguiente
  // sesión no herede la identidad del usuario que acaba de salir.
  if (hasContentsquareConfig() && window._uxa) {
    window._uxa.push(['session:clear:visitor']);
  }
}

/**
 * Registra un evento de negocio puntual (p. ej. "record_added",
 * "checkout_started"). No incluyas PII en los parámetros.
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (hasGaConfig() && window.gtag) {
    window.gtag('event', name, params ?? {});
  }
  if (hasContentsquareConfig() && window._uxa) {
    window._uxa.push(['trackPageEvent', name]);
  }
}

/**
 * Habilita la grabación NO anonimizada de Session Replay tras consentimiento
 * explícito del usuario. Por defecto Contentsquare graba anonimizado (texto
 * enmascarado); solo llamar a esto cuando el usuario haya aceptado. Para
 * revocar, pasar `false`.
 */
export function setReplayConsent(granted: boolean): void {
  if (!hasContentsquareConfig() || !window._uxa) return;
  window._uxa.push([
    granted ? 'replay:consent:unanonymized:granted' : 'replay:consent:unanonymized:withdrawn',
  ]);
}
